// Per-user settings for the Incomm VOID Report.
//
// These used to live in localStorage, which is per browser: the same person got
// defaults back on a second machine, and two people sharing a browser profile
// shared one preset. They now round-trip through /api/user-preferences against
// the logged-in user id.
//
// localStorage is still written as a mirror. It is never the source of truth —
// it only covers the gap before the first server response lands and the case
// where the API is unreachable, so the tile does not flash defaults on every
// load. A preset already sitting in a browser from the localStorage-only build
// is adopted once, on the first load that finds no server-side settings.

import { COLUMN_BY_ID, DEFAULT_TABLE_COLUMNS, DEFAULT_EXPORT_COLUMNS } from './columns';
import { DEFAULT_EXPORT_OPTIONS } from './csv';
import { PRESETS, defaultRange } from './components/DateRangePicker';

const API_URL = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');

const TILE_KEY = 'incomm_void_report';
const PREFS_URL = `${API_URL}/user-preferences/${TILE_KEY}`;

// Mirror + legacy keys. The two legacy keys are what the first build wrote.
const MIRROR_KEY = 'incommVoidReport.prefs';
const LEGACY_PRESET_KEY = 'incommVoidReport.exportPreset';
const LEGACY_TABLE_COLUMNS_KEY = 'incommVoidReport.tableColumns';

export const PREFS_VERSION = 1;

const authHeaders = (extra = {}) => {
  const token = localStorage.getItem('token');
  return { Authorization: token ? `Bearer ${token}` : '', ...extra };
};

export const defaultPrefs = () => ({
  version: PREFS_VERSION,
  tableColumns: [...DEFAULT_TABLE_COLUMNS],
  sort: { columnId: 'processDate', direction: 'desc' },
  pageSize: 50,
  // Stored as a preset id wherever one matches, so "Last 7 days" stays relative
  // instead of freezing into the dates that were current when it was saved.
  range: { presetId: 'last7' },
  filters: { status: 'all', voidedBy: 'all', carrier: 'all', reshipped: 'all' },
  exportScope: 'filtered',
  export: {
    selection: DEFAULT_EXPORT_COLUMNS.map((id) => ({ id, header: COLUMN_BY_ID[id].label })),
    options: { ...DEFAULT_EXPORT_OPTIONS },
  },
});

const readLocal = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const writeLocal = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
};

/**
 * Coerce whatever came back into a usable shape. Anything unrecognised falls
 * back to its default rather than throwing, so a stale or hand-edited blob
 * degrades to defaults one field at a time instead of breaking the page.
 */
export const sanitizePrefs = (raw) => {
  const base = defaultPrefs();
  if (!raw || typeof raw !== 'object') return base;

  const validIds = (list, fallback) => {
    if (!Array.isArray(list)) return fallback;
    const kept = list.filter((id) => COLUMN_BY_ID[id]);
    return kept.length ? kept : fallback;
  };

  const selection = Array.isArray(raw.export?.selection)
    ? raw.export.selection
        .filter((s) => s && COLUMN_BY_ID[s.id])
        .map((s) => ({ id: s.id, header: typeof s.header === 'string' ? s.header : COLUMN_BY_ID[s.id].label }))
    : [];

  const pageSize = [25, 50, 100, 250, 500].includes(raw.pageSize) ? raw.pageSize : base.pageSize;

  const range = raw.range && typeof raw.range === 'object'
    ? (PRESETS.some((p) => p.id === raw.range.presetId)
        ? { presetId: raw.range.presetId }
        : (/^\d{4}-\d{2}-\d{2}$/.test(raw.range.startDate || '') && /^\d{4}-\d{2}-\d{2}$/.test(raw.range.endDate || '')
            ? { startDate: raw.range.startDate, endDate: raw.range.endDate }
            : base.range))
    : base.range;

  const oneOf = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);

  return {
    version: PREFS_VERSION,
    tableColumns: validIds(raw.tableColumns, base.tableColumns),
    sort: {
      columnId: COLUMN_BY_ID[raw.sort?.columnId] ? raw.sort.columnId : base.sort.columnId,
      direction: oneOf(raw.sort?.direction, ['asc', 'desc'], base.sort.direction),
    },
    pageSize,
    range,
    filters: {
      status: oneOf(raw.filters?.status, ['all', 'V', 'S'], 'all'),
      reshipped: oneOf(raw.filters?.reshipped, ['all', 'yes', 'no'], 'all'),
      // These two are free-form (a username / a carrier name), so they are kept
      // as given but must still be strings.
      voidedBy: typeof raw.filters?.voidedBy === 'string' ? raw.filters.voidedBy : 'all',
      carrier: typeof raw.filters?.carrier === 'string' ? raw.filters.carrier : 'all',
    },
    exportScope: oneOf(raw.exportScope, ['filtered', 'all'], base.exportScope),
    export: {
      selection: selection.length ? selection : base.export.selection,
      options: { ...DEFAULT_EXPORT_OPTIONS, ...(raw.export?.options || {}) },
    },
  };
};

/** Fold a pre-server localStorage preset into the new shape, once. */
const adoptLegacyLocalPrefs = () => {
  const legacyPreset = readLocal(LEGACY_PRESET_KEY);
  const legacyColumns = readLocal(LEGACY_TABLE_COLUMNS_KEY);
  if (!legacyPreset && !legacyColumns) return null;

  const base = defaultPrefs();
  return sanitizePrefs({
    ...base,
    tableColumns: Array.isArray(legacyColumns) ? legacyColumns : base.tableColumns,
    export: legacyPreset && Array.isArray(legacyPreset.selection)
      ? { selection: legacyPreset.selection, options: legacyPreset.options || {} }
      : base.export,
  });
};

/** What to render with before the network answers. */
export const readMirror = () => {
  const mirrored = readLocal(MIRROR_KEY);
  if (mirrored) return sanitizePrefs(mirrored);
  const legacy = adoptLegacyLocalPrefs();
  return legacy || defaultPrefs();
};

/**
 * Fetch this user's saved settings.
 * @returns {Promise<{prefs, source}>} source is 'server' | 'legacy' | 'default'
 */
export const fetchPreferences = async (signal) => {
  const res = await fetch(PREFS_URL, { headers: authHeaders(), signal });
  if (!res.ok) throw new Error(`Could not load your saved settings (${res.status})`);
  const body = await res.json();

  if (body.prefs) {
    const prefs = sanitizePrefs(body.prefs);
    writeLocal(MIRROR_KEY, prefs);
    return { prefs, source: 'server' };
  }

  // Nothing stored for this user yet. If this browser holds a preset from the
  // localStorage-only build, take it over so the upgrade is not a reset.
  const legacy = adoptLegacyLocalPrefs();
  if (legacy) {
    writeLocal(MIRROR_KEY, legacy);
    return { prefs: legacy, source: 'legacy' };
  }
  return { prefs: defaultPrefs(), source: 'default' };
};

export const putPreferences = async (prefs) => {
  writeLocal(MIRROR_KEY, prefs);
  const res = await fetch(PREFS_URL, {
    method: 'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ prefs }),
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).message || ''; } catch (_) {}
    throw new Error(detail || `Could not save your settings (${res.status})`);
  }
  return true;
};

export const deletePreferences = async () => {
  try { localStorage.removeItem(MIRROR_KEY); } catch (_) {}
  try { localStorage.removeItem(LEGACY_PRESET_KEY); } catch (_) {}
  try { localStorage.removeItem(LEGACY_TABLE_COLUMNS_KEY); } catch (_) {}
  const res = await fetch(PREFS_URL, { method: 'DELETE', headers: authHeaders() });
  if (!res.ok) throw new Error(`Could not reset your settings (${res.status})`);
  return true;
};

/** Turn a stored range preference into concrete dates for this moment. */
export const resolveRange = (rangePref) => {
  if (rangePref?.presetId) {
    const preset = PRESETS.find((p) => p.id === rangePref.presetId);
    if (preset) return preset.range();
  }
  if (rangePref?.startDate && rangePref?.endDate) {
    return { startDate: rangePref.startDate, endDate: rangePref.endDate };
  }
  return defaultRange();
};

/** Store a range as a preset id when it matches one, else as explicit dates. */
export const rangeToPref = ({ startDate, endDate }) => {
  const preset = PRESETS.find((p) => {
    const r = p.range();
    return r.startDate === startDate && r.endDate === endDate;
  });
  return preset ? { presetId: preset.id } : { startDate, endDate };
};
